import { LoadingSpinner } from "components/LoadingSpinner";
import useDocumentTitle from "hooks/useDocumentTitle";
import RuleHistory from "pages/PayerRules/components/RuleHistory";
import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useGetDiscountRulesetByIdQuery } from "store/queries/procedureDiscountRuleset";

import { AllowDuplicateCptCodes } from "./components/AllowDuplicateCptCodes";
import { ContractLanguageField } from "./components/ContractLanguageField";
import { CptCodes } from "./components/CptCodes";
import { DiscountPercents } from "./components/DiscountPercents";
import { Header } from "./components/Header";
import { ProviderAndPayerEditSection } from "./components/ProviderAndPayerEditSection";
import { RuleName } from "./components/RuleName";
import { SubmitEdit } from "./components/SubmitEdit";
import {
  editFormScope,
  savedApplicabilityOf,
} from "./helpers/ruleApplicability";
import { RuleBucketProvider } from "./hooks/useRuleBucket";

export function EditPayerRulesPage() {
  useDocumentTitle("Edit Payer Rules");
  const { id } = useParams();
  const { data: ruleset, isLoading } = useGetDiscountRulesetByIdQuery(id);

  const methods = useForm({
    defaultValues: {
      // CLE-2842 step 2 — applicability, provider, payer (All payers) and a Fallback's payer.
      ...editFormScope(ruleset?.data),
      groupName: ruleset?.data.groupName,
      cptCodes: ruleset?.data.cptCodes,
      discountPercentForRank2: ruleset?.data.discountPercentForRank2,
      discountPercentForRank3: ruleset?.data.discountPercentForRank3,
      discountPercentForRank4: ruleset?.data.discountPercentForRank4,
      discountPercentForRank5Plus: ruleset?.data.discountPercentForRank5Plus,
      allowOnDuplicateCptCodes: ruleset?.data.allowOnDuplicateCptCodes,
      duplicateNotPaid: ruleset?.data.duplicateNotPaid,
      contractLanguage: ruleset?.data.contractLanguage ?? "",
    },
  });

  useEffect(() => {
    if (ruleset) {
      methods.reset({
        ...editFormScope(ruleset?.data),
        groupName: ruleset?.data.groupName,
        cptCodes: ruleset?.data.cptCodes,
        discountPercentForRank2: ruleset?.data.discountPercentForRank2,
        discountPercentForRank3: ruleset?.data.discountPercentForRank3,
        discountPercentForRank4: ruleset?.data.discountPercentForRank4,
        discountPercentForRank5Plus: ruleset?.data.discountPercentForRank5Plus,
        allowOnDuplicateCptCodes: ruleset?.data.allowOnDuplicateCptCodes,
        duplicateNotPaid: ruleset?.data.duplicateNotPaid,
        contractLanguage: ruleset?.data.contractLanguage ?? "",
      });
    }
  }, [ruleset, methods]);

  const savedApplicability = savedApplicabilityOf(ruleset?.data);

  if (isLoading) {
    return <LoadingSpinner />;
  }
  return (
    // CLE-2842 — the row's `bucketMembers` say which other payers share this rule.
    <RuleBucketProvider row={ruleset?.data}>
      <FormProvider {...methods}>
        <Header name={ruleset?.data.groupName} />
        <ProviderAndPayerEditSection savedApplicability={savedApplicability} />
        <RuleName />
        <AllowDuplicateCptCodes />
        <CptCodes />
        <DiscountPercents />
        <ContractLanguageField
          ruleType="MPPR"
          ruleId={id}
          attachments={ruleset?.data.attachments}
        />
        {id && ruleset && (
          <SubmitEdit id={id} savedApplicability={savedApplicability} />
        )}
        <RuleHistory histories={ruleset?.data.history} />
      </FormProvider>
    </RuleBucketProvider>
  );
}
